import * as React from 'react';
import { useState, ReactNode, useEffect } from "react";
import { useClickOutsideRef } from './UseClickOutsideRef';
import { SearchBox } from './SearchBox';
import { ToggleButton } from './ToggleButton';
import { LabelledCheckbox } from './LabelledCheckbox';

export interface IListProps<T> {
    toggleSelected?: (item: T) => void,
    onClick?: (item: T) => void,
    listSize?: number,
    maxItems?: number,
    isSelected?: (item: T) => true | false | 'partial',
    filterAsync?: (name: string, items?: T[]) => Promise<T[] | null>,
    filter?: (name: string, items?: T[]) => T[],
    itemFilter?: (item: T, filterText: string) => boolean,
    expanded?: boolean,
    setExpanded?: (value: boolean) => void,
    filterText?: string,
    setFilterText?: (value: string) => void,
    refreshFilterOnDataSourceChange?: boolean
    dataSource?: T[],
    displayLoadingData?: boolean,
    selected: T[],
    filterTextPlaceholder?: string,
    listItemType: any,
    selectItems?: (items: T[]) => void,
    deselectItems?: (items: T[]) => void,
    noResultsMessage: string,
    buildKey: (item: T) => any,
    summary?: string,
    searchDisabled?: boolean,
    searchDelayMs?: number,
    invalid?: boolean
}

export const List = <T,>(props: IListProps<T>) => {
    const [filteredItems, setFilteredItems] = useState(props.dataSource);
    const [showAll, setShowAll] = useState(false);
    const ListItemType = props.listItemType;

    useEffect(() => {
        if (
            filteredItems &&
            filteredItems.length > 0 &&
            props.filterText &&
            props.refreshFilterOnDataSourceChange
        ) {
            filter(props.filterText);
            return;
        }

        setFilteredItems(props.dataSource);
    }, [props.dataSource]);

    const collapse = () => {
        if (props.expanded && props.setExpanded) {
            props.setExpanded(false);
        }
    };

    const expand = () => {
        if (!props.expanded && props.setExpanded) {
            props.setExpanded(true);
        }
    };

    const toggleList = () => {
        if (props.setExpanded) {
            props.setExpanded(!props.expanded);
        }
    };

    const autoCloseRef = useClickOutsideRef<HTMLDivElement>(collapse);

    const filter = (name: string) => {
        setShowAll(false);

        if (props.setFilterText) {
            props.setFilterText(name);
        }

        if (props.filter) {
            setFilteredItems(props.filter(name, props.dataSource));
            expand();
        }

        if (props.filterAsync) {
            props.filterAsync(name, props.dataSource).then(result => {
                if (result) {
                    setFilteredItems(result);

                    expand();
                }
            });
        }
    };

    const isSelected = (item: T) => {
        if (props.isSelected) {
            return props.isSelected(item);
        }

        if (item) {
            if (props.buildKey) {
                const itemKey = props.buildKey(item);

                return props.selected.findIndex(i => props.buildKey(i) === itemKey) > -1;
            } else {
                return props.selected.indexOf(item);
            }
        }

        return false;
    };

    const selectAll = () => {
        var partialSelected = (filteredItems ?? []).some(f => isSelected(f) === false);

        if (props.selectItems && partialSelected) {
            props.selectItems(filteredItems ?? []);
        } else {
            if (props.deselectItems) {
                props.deselectItems(filteredItems ?? []);
            }
        }
    };

    const itemFilter = (item: T) => {
        if (!props.itemFilter) return true;

        if (!props.filterText) return true;

        return props.itemFilter(item, props.filterText);
    };

    return (
        <div ref={autoCloseRef} className={"list-container" + (props.maxItems && !showAll ? " limited" : "")}>

            {props.displayLoadingData && (props.dataSource == undefined || props.dataSource?.length < 1) ?
                <div className="list-container-loading">Loading...</div>
                :
                <div className={`list-filter${props.invalid ? ' is-invalid' : ''}`}>
                    <SearchBox
                        search={filter}
                        placeholder={props.filterTextPlaceholder}
                        onChange={props.setFilterText}
                        value={props.filterText}
                        searchDisabled={props.searchDisabled}
                        searchDelayMs={props.searchDelayMs}
                    />
                    <ToggleButton isOpen={props.expanded} onClick={toggleList} />
                </div>
            }

            {props.expanded &&
                <div className={"list-popup " + (props.expanded ? "expanded" : "collapsed")} >
                    {
                        props.summary &&
                        <div className="list-filter-summary">{props.summary}</div>
                    }

                    {(filteredItems && filteredItems.length > 1 && props.selectItems) &&
                        <div className={"list-item select-all"} onClick={selectAll}>
                            <LabelledCheckbox checkedValue={!filteredItems.some(f => isSelected(f) === false)}>Select all</LabelledCheckbox>
                        </div>
                    }

                    {filteredItems && filteredItems.length > 0 &&
                        filteredItems.filter(itemFilter).filter(
                            (item, index) => (((props.maxItems ?? 0) > 0) && !showAll) ? (index < (props.maxItems ?? 0)) : true
                        ).map(
                            (item, index) =>
                                <ListItemType
                                    key={props.buildKey(item)}
                                    item={item}
                                    onClick={props.onClick}
                                    toggle={props.toggleSelected}
                                    selectedItems={props.selected}
                                    previousItem={index > 0 ? filteredItems[index - 1] : null}
                                    selected={isSelected(item)} />
                        )
                    }

                    {!showAll && filteredItems && props.maxItems && (filteredItems.length > props.maxItems) &&
                        <div className="show-more" onClick={() => setShowAll(true)}>{filteredItems.length - props.maxItems} more items, show more...</div>
                    }
                </div>
            }
        </div>
    );
};

export interface IListItemProps<T> {
    item: T,
    toggle: (item: T) => void,
    children?: ReactNode,
    selected: true | false | 'partial',
    selectedItems?: T[],
    indent?: number,
    previousItem?: T
}

export const ListItem = <T,>(props: IListItemProps<T>) => {
    return (
        <div className="list-item" onClick={() => props.toggle(props.item)}>{props.children}</div>
    );
};

export const StringListItem = (props: IListItemProps<string>) => {
    return (
        <ListItem {...props}>
            <div>
                <LabelledCheckbox checkedValue={props.selected === true}>
                    {props.item}
                </LabelledCheckbox>
            </div>
        </ListItem>
    );
};